import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { EpcItemGerado } from "@/types/epc-item-gerado";
import type { ListaZplItem } from "@/types/lista-zpl-item";
import type { LocalizacaoItem } from "@/types/localizacao-nv1-item";
import type { LocalizacaoNv2Item } from "@/types/localizacao-nv2-item";
import type { LocalizacaoNv3Item } from "@/types/localizacao-nv3-item";
import type { LocalizacaoNv4Item } from "@/types/localizacao-nv4-item";
import type { LocalizacaoResponse } from "@/types/localizacoes-response";

export const verificarVariaveisDeSubstituicaoEmInfosComplementares = (
  epcItemGerado: EpcItemGerado,
  info_complementar: string = "",
) => {
  if (
    info_complementar &&
    info_complementar.toLocaleLowerCase() === "{n_impressao}"
  ) {
    return epcItemGerado.numero_impressao.toString();
  } else {
    return info_complementar;
  }
};

/**
 * Valor usado nos comboboxes (cmdk) de seleção de categoria.
 *
 * Formato: `${ean}|-|${descricao} ${inf_compl_1..12}`.
 * O ean continua sendo o primeiro segmento antes de `|-|`, então quem faz
 * `value.split("|-|")[0]` no onSelect continua funcionando. As informações
 * complementares vão no fim só para que a busca também encontre a categoria
 * por qualquer inf_compl (ex.: digitar "534324" acha a categoria cuja
 * inf_compl_1 é 534324).
 */
export function categoriaComboValue(categoria: CategoriaItemResponse): string {
  const infosComplementares = Array.from(
    { length: 12 },
    (_, i) => (categoria as unknown as Record<string, unknown>)[
      `inf_compl_${i + 1}`
    ],
  )
    .filter((v): v is string => typeof v === "string" && !!v.trim())
    .map((v) => v.trim())
    .join(" ");

  const base = `${categoria.ean}|-|${categoria.descricao}`;
  return infosComplementares ? `${base} ${infosComplementares}` : base;
}

export const verificarEpcsInvalidos = (listaEpcs: string[]) => {
  // Verifica se algum EPC é inválido (ex: vazio ou com caracteres não permitidos)
  const epcRegex = /^[0-9A-Fa-f]+$/; // Exemplo: apenas caracteres hexadecimais

  return listaEpcs.some((epc) => {
    // Se for "aguardando epc", considera válido
    if (epc.trim().toLowerCase().includes("aguardando epc")) {
      return false;
    }
    return !epcRegex.test(epc) || epc.trim() === "";
  });
};

const converteDPIParaDPMM = (dpmm: string) => {
  switch (dpmm) {
    case "203":
      return "8";
    case "300":
      return "12";
    case "600":
      return "24";
    default:
      return "8";
  }
};

const converterMmParaPolegadas = (millimeters: string) => {
  const parsedMillimeters = parseFloat(millimeters);
  if (!isNaN(parsedMillimeters)) {
    const inches = parsedMillimeters / 25.4;
    return inches.toFixed(2);
  }
};

export const converterListaZplItemParaUrl = (listaZplItem: ListaZplItem) => {
  let url = `${encodeURI(
    `https://api.labelary.com/v1/printers/${converteDPIParaDPMM(
      listaZplItem.dpmm,
    )}dpmm/labels/${converterMmParaPolegadas(
      listaZplItem.largura,
    )}x${converterMmParaPolegadas(listaZplItem.altura)}/0/${listaZplItem.zpl}`,
  )}`;
  return url;
};

type LocalizacaoNv4Node = LocalizacaoNv4Item;
type LocalizacaoNv3Node = LocalizacaoNv3Item & {
  children: LocalizacaoNv4Node[];
};
type LocalizacaoNv2Node = LocalizacaoNv2Item & {
  children: LocalizacaoNv3Node[];
};
export type LocalizacaoNv1Node = LocalizacaoItem & {
  children: LocalizacaoNv2Node[];
};

// Localizacao sem descricao nao tem como ser exibida nem selecionada (a
// descricao e o proprio valor do seletor) e ainda quebra o <SelectItem />, que
// exige value diferente de string vazia. Registro assim fica de fora da arvore.
function temDescricao(localizacao: { descricao?: string }): boolean {
  return typeof localizacao.descricao === "string"
    ? localizacao.descricao.trim() !== ""
    : false;
}

export function aninharLocalizacoes(
  data: LocalizacaoResponse,
): LocalizacaoNv1Node[] {
  const nivel4: LocalizacaoNv4Node[] =
    data.localizacoes_nivel_4.filter(temDescricao);

  const nivel3: LocalizacaoNv3Node[] = data.localizacoes_nivel_3
    .filter(temDescricao)
    .map((n3) => ({
      ...n3,
      children: nivel4.filter((n4) => n4.id_nivel_loc3 === n3._id),
    }));

  const nivel2: LocalizacaoNv2Node[] = data.localizacoes_nivel_2
    .filter(temDescricao)
    .map((n2) => ({
      ...n2,
      children: nivel3.filter((n3) => n3.id_nivel_loc2 === n2._id),
    }));

  const nivel1: LocalizacaoNv1Node[] = data.localizacoes_nivel_1
    .filter(temDescricao)
    .map((n1) => ({
      ...n1,
      children: nivel2.filter((n2) => n2.id_nivel_loc1 === n1._id),
    }));

  return nivel1;
}

export const formatarData = (data: string, hora: boolean = true) => {
  const dataObj = new Date(data);
  if (isNaN(dataObj.getTime())) return "";

  const dia = String(dataObj.getDate()).padStart(2, "0");
  const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
  const ano = dataObj.getFullYear();

  if (hora) {
    const horas = String(dataObj.getHours()).padStart(2, "0");
    const minutos = String(dataObj.getMinutes()).padStart(2, "0");
    const segundos = String(dataObj.getSeconds()).padStart(2, "0");
    return `${dia}/${mes}/${ano} ${horas}:${minutos}:${segundos}`;
  } else {
    return `${dia}/${mes}/${ano}`;
  }
};

export function brDateToISO(dateStr: string | undefined) {
  // Espera "DD/MM/AAAA" (aceita tambem "AAAA-MM-DD" e ISO completo)
  if (!dateStr) return "";
  const texto = dateStr.trim();
  if (!texto) return "";

  const br = texto.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (br) {
    const [, dia, mes, ano] = br;
    const jsDate = new Date(
      `${ano}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}T00:00:00`,
    );
    return isNaN(jsDate.getTime()) ? "" : jsDate.toISOString();
  }

  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const jsDate = new Date(`${texto}T00:00:00`);
    return isNaN(jsDate.getTime()) ? "" : jsDate.toISOString();
  }

  const jsDate = new Date(texto);
  return isNaN(jsDate.getTime()) ? "" : jsDate.toISOString();
}

// Sequencial dentro da sessao: junto com o timestamp garante nome unico.
let contadorImpressaoLegacy = 0;

/**
 * Dispara o download de um arquivo gerado no navegador.
 *
 * Dois cuidados que o Chrome/Firefox cobram e que faltavam aqui:
 * o <a> precisa estar no documento (Firefox ignora clique em elemento solto)
 * e o objectURL so pode ser revogado depois que o navegador terminou de ler o
 * blob - revogar no mesmo tick do clique e uma corrida.
 */
export function baixarArquivoGerado(conteudo: string, nome: string) {
  const blob = new Blob([conteudo], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nome;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 60000);
}

/** Os dois arquivos de uma ordem legacy, pro caso de o download automatico
 *  falhar e o operador precisar baixar de novo pela tela. */
export type ArquivosImpressaoLegacy = {
  txt: { nome: string; conteudo: string };
  bat: { nome: string; conteudo: string };
};

export function downloadImpressaoLegacy(
  txtLegacy: string,
  hardwareID: string,
): ArquivosImpressaoLegacy {
  // Nome unico por ordem. O Math.random()*9999 antigo colidia com facilidade:
  // na colisao o Chrome salvava "temp_zpl_123 (1).txt" e o .bat continuava
  // mandando pra impressora o "temp_zpl_123.txt" da ordem anterior.
  const id = `${Date.now().toString(36)}${(contadorImpressaoLegacy++).toString(
    36,
  )}`;
  const nome_bat = `printer_order_legacy_${id}.bat`;
  const nome_arquivo = `temp_zpl_${id}.txt`;

  let printer_order = "";
  printer_order += "cls\n";
  printer_order += 'cd /d "%~dp0"\n';
  printer_order += "net use lpt1 /delete\n";
  printer_order += `net use lpt1 ${hardwareID}\n`;
  printer_order += `type ${nome_arquivo} > lpt1\n`;
  printer_order += `timeout /t 10 /nobreak\n`;
  printer_order += `del ${nome_arquivo}\n`;
  printer_order += `del ${nome_bat}\n`;

  const arquivos: ArquivosImpressaoLegacy = {
    txt: { nome: nome_arquivo, conteudo: txtLegacy },
    bat: { nome: nome_bat, conteudo: printer_order },
  };
  baixarArquivosLegacy(arquivos);
  return arquivos;
}

/** Dispara os dois arquivos da ordem. O .bat sai num tick separado: dois
 *  cliques de download no mesmo tick caem no bloqueio de "baixar varios
 *  arquivos" do Chrome, que derruba o segundo sem erro nenhum na pagina. */
export function baixarArquivosLegacy(arquivos: ArquivosImpressaoLegacy) {
  baixarArquivoGerado(arquivos.txt.conteudo, arquivos.txt.nome);
  setTimeout(
    () => baixarArquivoGerado(arquivos.bat.conteudo, arquivos.bat.nome),
    700,
  );
}

export function extrairToken(api_externa: string): string {
  // Remove http(s)://, tudo até a última barra, e retorna só o token
  return api_externa
    .replace(/https?:\/\/.*\//, "")
    .replace(/\//g, "")
    .toLowerCase();
}

/**
 * Converte um código para uma string hexadecimal do mesmo comprimento.
 * Se o código já for hex válido, retorna como está (uppercase).
 * Caso contrário, aplica um hash determinístico — o mesmo código sempre
 * gera o mesmo resultado, garantindo consistência na criação de categorias.
 */
export function sanitizarCodigoHex(codigo: string): string {
  if (/^[0-9A-Fa-f]+$/.test(codigo)) return codigo.toUpperCase();

  const len = codigo.length;
  // djb2 hash
  let hash = 5381;
  for (let i = 0; i < codigo.length; i++) {
    hash = ((hash << 5) + hash + codigo.charCodeAt(i)) >>> 0;
  }
  // Expande o hash para cobrir o comprimento necessário (LCG)
  let result = "";
  let seed = hash;
  while (result.length < len) {
    seed = ((seed * 1664525 + 1013904223) & 0xffffffff) >>> 0;
    result += seed.toString(16).toUpperCase().padStart(8, "0");
  }
  return result.slice(0, len);
}

export function calculateSSCCCheckDigit(sscc: string) {
  if (sscc.length !== 17) {
    throw new Error("SSCC deve ter exatamente 17 dígitos.");
  }

  const digits = sscc.split("").map(Number);

  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const weight = i % 2 === 0 ? 3 : 1;
    sum += digits[16 - i] * weight;
  }

  const remainder = sum % 10;
  const checkDigit = remainder === 0 ? 0 : 10 - remainder;

  return checkDigit;
}
